// @flow
import React, { Component } from 'react'
import {
  customMapStyles,
  generateHeatmapColors,
  HEATMAP_FILL_OPACITY,
  HEATMAP_NO_PARTNERS_COLOR,
  LIMIT_WHEN_CLUSTER_CLICK_WILL_ZOOM
} from 'store/constants/constants'
import { GoogleApiWrapper, Map, Marker, Polygon } from 'google-maps-react'
import MarkerCluster from 'components/MarkerCluster'
import ElementSave from 'components/ElementSave'
import CustomCheckbox from 'components/ui/CustomCheckbox'
import { t } from 'ttag'
import styled from 'styled-components/macro'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import { bindActionCreators } from 'redux'
import { getHeatMapPartnerships } from 'store/actions/search'
import { connect } from 'react-redux'
import breakpoints from 'assets/stylesheet'
import PropTypes from 'prop-types'
import NonEuPartnerShipMarker from 'assets/images/red-circle.png'
import PartnerMarker from 'assets/images/marker.png'
import LeadPartnerMarker from 'assets/images/marker-orange.png'
import ClusterImage1 from 'assets/images/clusterimages/m1.png'
import ClusterImage2 from 'assets/images/clusterimages/m2.png'
import ClusterImage3 from 'assets/images/clusterimages/m3.png'
import ClusterImage4 from 'assets/images/clusterimages/m4.png'
import ClusterImage5 from 'assets/images/clusterimages/m5.png'

const MapContainer = styled.div`
  padding-left: 25px;
  position: relative;
  @media (max-width: ${breakpoints.bpDesktopPx}) {
    padding-left: 0;
  }
`
const SpinnerContainer = styled.div`
  position: relative;
  display: block;
  margin-top: 40px;
`
const StyledMap = styled(Map)`
  position: relative !important;
`

const MapActions = styled.div`
  display: flex;
`
const Input = styled.input`
  margin-right: 5px !important;
`
const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #4a4a4a;
  padding-right: 10px;
`
const MapInfo = styled.div`
  padding-top: 10px;
  color: #4a4a4a;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  line-height: 14px;
  p:not(.info) {
    margin-top: 2px !important;
    margin-bottom: 20px !important;
  }
`
const MapInfoSectionTitle = styled.legend`
  font-weight: bold;
  margin-bottom: 8px;
`
const ColorInfo = styled.div`
  display: flex;
  margin-bottom: 10px;
`
const ClusterInfo = styled.div`
  margin-bottom: 10px;
  @media (max-width: ${breakpoints.bpDesktopPx}) {
    display: block;
  }
`
const InfoText = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-style: italic;
  font-size: 12px;
  line-height: 16px;
  margin-top: ${props => (props.topMargin ? '20px' : '2px')};
`
const InfoItem = styled.div`
  margin-right: 10px;
  img {
    width: 18px;
    margin-right: 8px;
  }
  ${props =>
    props.alignCenter &&
    `
  display: flex;
  align-items: center;
  `}
`
const ClusterImg = styled.img`
  width: 25px !important;
  margin-right: 5px;
`

const DoubleCircle = styled.div`
  position: relative;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  float: left;
  margin-right: 5px;
  margin-top: -2px;
`

const CircleLayer = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.color || '#ccc'};
  opacity: ${props => props.opacity || 1};
  ${props =>
    props.bordered &&
    `
    box-sizing: border-box;
    border: 1px solid #b0b0b0;
  `}
`
const MapSaveButton = styled.a`
  background-color: #003399;
  padding: 5px;
  cursor: pointer;
  border-radius: 2px;
  color: white !important;
  font-size: 10px;
`

const LoadingContainer = () => <LoadingSpinner showText />

class PartnersMap extends Component {
  constructor(props) {
    super(props)
    const { google } = props

    this.infoWindow = new google.maps.InfoWindow()

    this.clusterRef = React.createRef()
    this.mapRef = React.createRef()

    // The two map layers are alternatives: the heat map is the one shown by
    // default, the all-partnerships map is opt-in. Both can be off, which is why
    // they stay checkboxes rather than radio buttons.
    this.state = {
      allPartnershipsSelected: false,
      heatMapSelected: true,
      showMapSaveModal: false,
      mapLoaded: false
    }

    this.setRef = this.setRef.bind(this)
    this.handleClusterClick = this.handleClusterClick.bind(this)
    this.handleMarkerClick = this.handleMarkerClick.bind(this)
    this.handleAllPartnershipsClick = this.handleAllPartnershipsClick.bind(this)
    this.handleHeatmapClick = this.handleHeatmapClick.bind(this)
  }

  componentDidMount() {
    const { allPartnershipsSelected } = this.state

    // For some reason (might be a compatibility issue with google-maps-react and Google maps marker cluster)
    // the marker clusters need to be re-rendered by toggling the allPartnershipsSelected state to show them in the map
    if (allPartnershipsSelected) {
      this.setState({ allPartnershipsSelected: false })
      setTimeout(() => this.setState({ allPartnershipsSelected: true }))
    }

    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('map-types')) {
      const mapTypes = urlParams.get('map-types')
      const pship = mapTypes.indexOf('all-partnership') > -1
      const heatmap = mapTypes.indexOf('heat') > -1
      // A map saved before the two layers became alternatives may carry both.
      // Normalise it here, otherwise the restored state would be one the UI can
      // no longer produce. The heat map wins, being the default layer.
      setTimeout(() =>
        this.setState({
          allPartnershipsSelected: pship && !heatmap,
          heatMapSelected: heatmap
        })
      )
    }
  }

  setRef(ref) {
    this.clusterRef.current = ref
  }

  showInfoWindow(markerIds, map, center) {
    const { getHeatMapPartnerships: getHeatMapPartnershipsAction } = this.props
    this.infoWindow.setContent(t`Loading...`)
    this.infoWindow.setPosition(center)
    this.infoWindow.open(map)
    const data = {
      body: { partner_ids: markerIds }
    }
    getHeatMapPartnershipsAction(data).then(response => {
      const infoTitle = `<div class="iw-title">Partners and Projects info</div>`
      let infoContent = '<div class="iw-content">'
      response.value.forEach(partnership => {
        infoContent += `
            <div class="iw-partner-container">
              <div class="iw-partner-name ${partnership.type}">${
                partnership.partner.name_translated || partnership.partner.name
              }</div>
              <p>
                <a href="/projects/${partnership.project_id}" target="_blank">
                  ${partnership.project}
                </a>
              </p>
            </div>
          `
      })
      infoContent += '</div>'
      this.infoWindow.setContent(`${infoTitle}${infoContent}`)
      // infoWindow.open() repositions the window so it needs to be called again after dynamic content has been loaded
      this.infoWindow.open(map)
    })
  }

handleClusterClick(cluster) {
    // 1. NEW API: Use the .markers property instead of .getMarkers()
    const markers = cluster.markers
    const { map } = this.mapRef.current
    
    if (markers.length > LIMIT_WHEN_CLUSTER_CLICK_WILL_ZOOM) {
      // 2. NEW API: Use the .position property instead of .getCenter()
      map.setCenter(cluster.position)
      map.setZoom(map.getZoom() + 1)
    } else {
      const markerIds = markers.map(m => m.id)
      this.showInfoWindow(markerIds, map, cluster.position)
    }
  }

  handleMarkerClick({ entry, marker }) {
    // This requires no changes!
    // 'entry' is still a standard google.maps.Marker, so .getPosition() is valid.
    const markerIds = [marker.id]
    this.showInfoWindow(markerIds, this.mapRef.current.map, entry.getPosition())
  }

  handleAllPartnershipsClick(allPartnershipsSelected) {
    this.setState({
      allPartnershipsSelected,
      // Turning this layer on turns the other one off: they are alternatives.
      ...(allPartnershipsSelected ? { heatMapSelected: false } : {})
    })
    if (!allPartnershipsSelected) {
      this.clearMarkers()
    }
  }

  handleHeatmapClick(heatMapSelected) {
    this.setState({
      heatMapSelected,
      ...(heatMapSelected ? { allPartnershipsSelected: false } : {})
    })
    if (heatMapSelected) {
      // The other layer is being switched off, so its markers have to go with it.
      this.clearMarkers()
    }
  }
clearMarkers() {
    if (this.clusterRef.current != null) {
      // 1. Wipes all markers out of the clusterer and automatically removes them from the map viewport
      this.clusterRef.current.clearMarkers()

      // 2. Completely detaches the clusterer instance itself from the Google Map instance
      this.clusterRef.current.setMap(null)
      // 3. Wipes your React ref tracker as before
      this.setRef(null)
    }
  }

  handlePaths(paths) {
    const ps = []
    const checkPath = p => {
      p.forEach(current => {
        if (Array.isArray(current) && typeof current[0].lat) {
          ps.push(current)
        } else {
          current.forEach(p2 => checkPath(p2))
        }
      })
    }
    paths.forEach(p => checkPath(p))
    return ps
  }

  render() {
    const {
      google,
      search: { heatMapSearchResults, isHeatMapPending },
      isSidebarVisible
    } = this.props
    const { allPartnershipsSelected, heatMapSelected, showMapSaveModal, mapLoaded } = this.state
    const categoriesLabels = heatMapSearchResults.categories_labels || []
    const heatmapColors = generateHeatmapColors(categoriesLabels.length)
    const cookies = document.cookie.split('; ')
    const authenticated = cookies.some(c => c.startsWith('keep_auth_token'))
    const mapTypes = []
    const urlParams = new URLSearchParams(window.location.search)
    if (allPartnershipsSelected) {
      mapTypes.push('all-partnership')
    }
    if (heatMapSelected) {
      mapTypes.push('heat')
    }
    if (isHeatMapPending)
      return (
        <SpinnerContainer>
          <LoadingSpinner showText={false} />
        </SpinnerContainer>
      )
    return (
      <MapContainer>
        {authenticated && (
          <>
            <MapSaveButton
              onClick={() => this.setState({ showMapSaveModal: true })}
            >{t`Save map`}</MapSaveButton>
            <ElementSave
              type="map"
              show={showMapSaveModal}
              isSidebarVisible={isSidebarVisible}
              zoom={this.mapRef.current ? this.mapRef.current.map.zoom : 0}
              center={
                this.mapRef.current
                  ? {
                      lat: this.mapRef.current.map.center.lat(),
                      lng: this.mapRef.current.map.center.lng()
                    }
                  : { lat: 0, lng: 0 }
              }
              closeElementSave={() =>
                this.setState({
                  showMapSaveModal: false
                })
              }
              mapTypes={mapTypes}
            />
          </>
        )}
        <MapActions>
          <CustomCheckbox>
            <Input
              type="checkbox"
              id="all-partnerships-map"
              name="all-partnerships-map"
              value={allPartnershipsSelected}
              checked={allPartnershipsSelected ? 'checked' : ''}
              onChange={() => this.handleAllPartnershipsClick(!allPartnershipsSelected)}
            />
            <Label htmlFor="all-partnerships-map">{t`All partnerships map`}</Label>
          </CustomCheckbox>
          <CustomCheckbox>
            <Input
              type="checkbox"
              id="heatmap"
              name="heatmap"
              value={heatMapSelected}
              checked={heatMapSelected ? 'checked' : ''}
              onChange={() => this.handleHeatmapClick(!heatMapSelected)}
            />
            <Label htmlFor="heatmap">{t`Heat map (partner concentration)`}</Label>
          </CustomCheckbox>
        </MapActions>
        <div id="results-map" data-map={mapLoaded ? 'loaded' : 'none'}>
          <StyledMap
            ref={this.mapRef}
            initialCenter={{
              lat: urlParams.has('center_lat')
                ? parseFloat(urlParams.get('center_lat'))
                : 52.519325,
              lng: urlParams.has('center_lng') ? parseFloat(urlParams.get('center_lng')) : 13.392709
            }}
            className="map"
            google={google}
            mapTypeControl={false}
            streetViewControl={false}
            fullscreenControl={window.location.href.indexOf('hide-control=true') === -1}
            zoomControl={window.location.href.indexOf('hide-control=true') === -1}
            styles={customMapStyles}
            style={{
              position: 'relative',
              height: '517px',
              width: '100%'
            }}
            zoom={urlParams.has('zoom') ? parseInt(urlParams.get('zoom'), 10) : 3}
            onTilesloaded={() => {
              this.setState({ mapLoaded: true })
              setTimeout(() => {
                const images = document.querySelectorAll('#results-map img')
                images.forEach(image => {
                  image.alt = 'Google Maps Image' // eslint-disable-line
                })
              }, 100)
            }}
          >
            {heatMapSelected &&
              heatMapSearchResults.non_eu_partnerships.length > 0 &&
              heatMapSearchResults.non_eu_partnerships.map((p, idx) => (
                <Marker
                  key={idx}
                  icon={{
                    url: NonEuPartnerShipMarker,
                    anchor: new google.maps.Point(4, 4),
                    scaledSize: new google.maps.Size(8, 8)
                  }}
                  position={{
                    lat: p.location.lat,
                    lng: p.location.lng
                  }}
                />
              ))}
            {heatMapSelected &&
              heatMapSearchResults.regions.length > 0 &&
              heatMapSearchResults.regions.map(region => (
                <Polygon
                  key={region.nuts_id}
                  paths={this.handlePaths(region.paths)}
                  strokeColor="#556B7D"
                  strokeOpacity={0.4}
                  strokeWeight={1.2}
                  fillColor={heatmapColors[region.display_category].hex}
                  fillOpacity={heatmapColors[region.display_category].opacity}
                />
              ))}
            {allPartnershipsSelected &&
              Object.keys(heatMapSearchResults.all_partnerships).length > 0 && (
                <MarkerCluster
                  markers={heatMapSearchResults.all_partnerships}
                  onClusterClick={this.handleClusterClick}
                  onMarkerClick={this.handleMarkerClick}
                  setRef={this.setRef}
                  currentCluster={this.clusterRef}
                />
              )}
          </StyledMap>
        </div>
        {allPartnershipsSelected && (
          <MapInfo>
            <MapInfoSectionTitle>{t`All partnerships map`}</MapInfoSectionTitle>
            <ColorInfo>
              <InfoItem alignCenter>
                <img alt={t`Lead partner`} src={LeadPartnerMarker} /> {t`Lead partner`}
              </InfoItem>
              <InfoItem alignCenter>
                <img alt={t`Project partner`} src={PartnerMarker} /> {t`Project partner`}
              </InfoItem>
            </ColorInfo>
            <div>
              <ClusterInfo>
                <InfoItem alignCenter>
                  <ClusterImg alt={t`2-9 markers`} src={ClusterImage1} /> {t`2-9 markers`}
                </InfoItem>
                <InfoItem alignCenter>
                  <ClusterImg alt={t`10-100 markers`} src={ClusterImage2} /> {t`10-100 markers`}
                </InfoItem>
                <InfoItem alignCenter>
                  <ClusterImg alt={t`101-1 000 markers`} src={ClusterImage3} />{' '}
                  {t`101-1 000 markers`}
                </InfoItem>
                <InfoItem alignCenter>
                  <ClusterImg alt={t`1 001-10 000 markers`} src={ClusterImage4} />{' '}
                  {t`1 001-10 000 markers`}
                </InfoItem>
                <InfoItem alignCenter>
                  <ClusterImg alt={t`10 001-> markers`} src={ClusterImage5} /> {t`10 001-> markers`}
                </InfoItem>
              </ClusterInfo>
            </div>
          </MapInfo>
        )}
        {heatMapSelected && (
          <MapInfo>
            <MapInfoSectionTitle>{t`Heat map`}</MapInfoSectionTitle>
            <InfoText>{t`Colours are selected to be colour-blind accessible. Colours indicate the concentration of partners across regions, from none (white) through low (yellow) to very high (black). There are up to 5 categories, but not all may be visible since only relevant categories are shown for each search result:`}</InfoText>
            <ColorInfo style={{ flexWrap: 'wrap', gap: '8px' }}>
              {/*
                "No partners" is rendered as the first class of the legend so the
                scale reads as one uninterrupted white -> black ramp. It has no
                counterpart in the search response: regions without partners are
                not returned at all, they are simply never drawn and the map
                background shows through.
              */}
              <InfoItem>
                <DoubleCircle>
                  <CircleLayer color="#ffffff" />
                  <CircleLayer
                    color={HEATMAP_NO_PARTNERS_COLOR}
                    opacity={HEATMAP_FILL_OPACITY}
                    bordered
                  />
                </DoubleCircle>
                {t`No partners`}
              </InfoItem>
              {categoriesLabels.map((label, idx) => (
                <InfoItem key={idx}>
                  <DoubleCircle>
                    <CircleLayer color="#ffffff" />
                    <CircleLayer
                      color={heatmapColors[idx].hex}
                      opacity={heatmapColors[idx].opacity}
                      bordered
                    />
                  </DoubleCircle>
                  {label}
                </InfoItem>
              ))}
            </ColorInfo>
          </MapInfo>
        )}
      </MapContainer>
    )
  }
}

PartnersMap.propTypes = {
  google: PropTypes.object,
  getHeatMapPartnerships: PropTypes.func,
  isSidebarVisible: PropTypes.bool,
  search: PropTypes.shape({
    isHeatMapPending: PropTypes.bool,
    heatMapSearchResults: PropTypes.object,
    error: PropTypes.bool
  })
}

const mapStateToProps = ({ search }) => ({
  search
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      getHeatMapPartnerships
    },
    dispatch
  )

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  GoogleApiWrapper({
    apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    LoadingContainer,
    libraries: ['places', 'visualization']
  })(PartnersMap)
)
